<script lang="ts">
export default {
  name: 'NotificationPage',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import notificationService from '@/services/v2/NotificationService';
import { UserNotification } from '@/types/Notifications';
import { relativeDateTime } from '@/utilities/Dates';
import {
  MoreHorizontal,
  Dot,
  Settings,
  CheckCheck,
  Bell,
  BellDot,
  ChevronLeft,
  ChevronRight,
  CalendarClock,
} from '@lucide/vue';
import MenuDropdown, { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import {
  ContextMenuRoot,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuPortal,
} from 'reka-ui';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { AppState, Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { sanitizeHtml } from '@/utilities/Sanitization';
import { routeFromNotification } from '@/utilities/NotificationRouting';
import { addCommas } from '@/utilities/Number';
import { isDashboardSubscriptionsEnabled } from '@/utilities/FeatureFlags';

const PAGE_SIZE = 20;

// -----
// refs
// -----

const store = useStore<AppState>();
const router = useRouter();
const showUnreadOnly = ref(false);
const items = ref<UserNotification[]>([]);
const total = ref(0);
const currentPage = ref(1);

// ---------
// computed
// ---------

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)));
const notifications = computed(() => items.value);
const hasUnread = computed(() => (showUnreadOnly.value ? total.value > 0 : items.value.some((n) => !n.isRead)));

// ---------
// lifecycle
// ---------

onMounted(loadPage);
watch(currentPage, loadPage);
watch(showUnreadOnly, () => {
  if (currentPage.value !== 1) {
    currentPage.value = 1;
  } else {
    loadPage();
  }
});

// ---------------------
// handlers / functions
// ---------------------

async function loadPage(): Promise<void> {
  const offset = (currentPage.value - 1) * PAGE_SIZE;
  const res = await notificationService.getNotifications({
    limit: PAGE_SIZE,
    offset,
    unreadOnly: showUnreadOnly.value,
  });
  items.value = res.notifications;
  total.value = res.total;
}

function updateItems(updater: (notifications: UserNotification[]) => UserNotification[]): void {
  items.value = updater(items.value);
}

function syncStoreReadState(id: number, isRead: boolean): void {
  Commit(
    StoreMutationType.SET_NOTIFICATIONS,
    store.state.notifications.map((n) => (n.id === id ? { ...n, isRead } : n)),
  );
}

function setReadState(id: number, isRead: boolean): void {
  updateItems((all) => all.map((n) => (n.id === id ? { ...n, isRead } : n)));
  syncStoreReadState(id, isRead);
}

async function handleDeleteNotification(notification: UserNotification): Promise<void> {
  await notificationService.deleteNotification(notification.id);
  Commit(
    StoreMutationType.SET_NOTIFICATIONS,
    store.state.notifications.filter((n) => n.id !== notification.id),
  );
  if (currentPage.value > 1 && items.value.length === 1) {
    currentPage.value -= 1;
  } else {
    await loadPage();
  }
}

async function handleMarkAsRead(notification: UserNotification): Promise<void> {
  await notificationService.markAsRead(notification.id);
  syncStoreReadState(notification.id, true);
  if (showUnreadOnly.value) {
    await loadPage();
  } else {
    updateItems((all) => all.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));
  }
}

async function handleMarkAsUnread(notification: UserNotification): Promise<void> {
  await notificationService.markAsUnread(notification.id);
  setReadState(notification.id, false);
}

async function handleMarkAllAsRead(): Promise<void> {
  await notificationService.markAllAsRead();
  Commit(
    StoreMutationType.SET_NOTIFICATIONS,
    store.state.notifications.map((n) => ({ ...n, isRead: true })),
  );
  if (showUnreadOnly.value) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      await loadPage();
    }
  } else {
    updateItems((all) => all.map((n) => ({ ...n, isRead: true })));
  }
}

function goToPage(page: number): void {
  const clamped = Math.min(Math.max(1, page), totalPages.value);
  if (clamped !== currentPage.value) {
    currentPage.value = clamped;
  }
}

async function handleNotificationClick(notification: UserNotification): Promise<void> {
  if (!notification.isRead) {
    await handleMarkAsRead(notification);
  }
  await routeFromNotification(router, notification);
}

function sanitizeNotificationMessage(message: string): string {
  return sanitizeHtml(message);
}

function optionsForNotification(notification: UserNotification): MenuItem[] {
  return [
    notification.isRead
      ? { label: 'Mark as Unread', handler: handleMarkAsUnread }
      : { label: 'Mark as Read', handler: handleMarkAsRead },
    { label: 'Delete Notification', handler: handleDeleteNotification },
  ];
}
</script>

<template>
  <div class="relative flex justify-center">
    <div class="layout gap-4 p-10">
      <div class="sidebar">
        <div class="sticky top-20">
          <section class="flex flex-col gap-1 rounded-xl border border-solid border-gray-200 p-4 drop-shadow-xs">
            <h1 class="mb-1 text-lg">Manage Your Notifications</h1>
            <a
              class="flex items-center gap-1"
              :class="hasUnread ? 'cursor-pointer' : 'pointer-events-none cursor-not-allowed opacity-50'"
              @click="hasUnread && handleMarkAllAsRead()">
              <CheckCheck :size="16" />
              <span>Mark all as read</span>
            </a>
            <a
              class="flex cursor-pointer items-center gap-1"
              @click="showUnreadOnly = !showUnreadOnly">
              <component
                :is="showUnreadOnly ? Bell : BellDot"
                :size="16" />
              <span>{{ showUnreadOnly ? 'Show all notifications' : 'Show unread only' }}</span>
            </a>

            <div class="divider my-0.5"></div>

            <router-link class="name" to="/notifications/preferences">
              <div class="flex items-center gap-1">
                <Settings :size="16" />
                <span>Notification Settings</span>
              </div>
            </router-link>
            <router-link
              v-if="isDashboardSubscriptionsEnabled()"
              class="name"
              to="/notifications/subscriptions">
              <div class="flex items-center gap-1">
                <CalendarClock :size="16" />
                <span>Dashboard Subscriptions</span>
              </div>
            </router-link>
          </section>
        </div>
      </div>
      <main class="main">
        <div class="relative rounded-xl border border-solid border-gray-200 drop-shadow-xs">
          <div v-if="notifications.length === 0" class="min-h-full min-w-full p-10">
            <h3>No New Notifications</h3>
          </div>
          <section class="flex flex-col items-center">
            <ContextMenuRoot
              v-for="notification of notifications"
              :key="notification.id">
              <ContextMenuTrigger as-child>
                <article
                  class="w-full cursor-pointer border-x-0 border-t-0 border-b-1 border-solid border-gray-200 p-4 pl-1 transition-colors first:rounded-t-xl last:rounded-b-xl last:border-0"
                  :class="{
                    'bg-base-100': notification.isRead,
                    'hover:bg-base-200': notification.isRead,
                    'border-gray-300 bg-blue-100': !notification.isRead,
                    'hover:bg-blue-200/70': !notification.isRead,
                  }"
                  @click="handleNotificationClick(notification)">
                  <div class="flex w-full gap-2">
                    <div :class="{ invisible: notification.isRead }" class="flex self-center">
                      <Dot :size="40" class="text-blue-700" />
                    </div>
                    <div class="text-md flex w-full max-w-md flex-wrap items-center text-pretty">
                      <p v-html="sanitizeNotificationMessage(notification.message)"></p>
                    </div>
                    <div class="w-fit-content flex flex-col items-end justify-self-end text-right text-[9px]">
                      <p class="text-end">{{ relativeDateTime(notification.createdAt) }}</p>
                      <MenuDropdown :items="optionsForNotification(notification)">
                        <template #action>
                          <button class="btn btn-circle btn-ghost btn-sm">
                            <MoreHorizontal class="h-7 w-7" />
                          </button>
                        </template>
                        <template #items="{ items, handleClick }">
                          <li v-for="item in items" :key="item.label" class="p-1">
                            <a
                              @click.stop="
                                () => {
                                  handleClick();
                                  item.handler(notification);
                                }
                              "
                              class="block w-full cursor-pointer rounded-lg px-4 py-2 text-sm text-gray-700 no-underline hover:bg-gray-200">
                              {{ item.label }}
                            </a>
                          </li>
                        </template>
                      </MenuDropdown>
                    </div>
                  </div>
                </article>
              </ContextMenuTrigger>
              <ContextMenuPortal to="body">
                <ContextMenuContent
                  class="max-w-80 min-w-60 gap-1 rounded-md bg-white p-2 text-sm outline-1 outline-gray-300 drop-shadow-xl">
                  <ContextMenuItem
                    v-for="item in optionsForNotification(notification)"
                    :key="item.label"
                    class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                    @click="item.handler(notification)">
                    {{ item.label }}
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenuPortal>
            </ContextMenuRoot>
          </section>
        </div>
        <div v-if="notifications.length > 0" class="mt-4 flex items-center justify-between text-sm text-gray-700">
          <span>
            Showing {{ addCommas((currentPage - 1) * PAGE_SIZE + 1) }}–{{ addCommas(Math.min(currentPage * PAGE_SIZE, total)) }} of {{ addCommas(total) }}
          </span>
          <div class="flex items-center gap-2">
            <button
              class="btn btn-sm btn-ghost"
              :disabled="currentPage <= 1"
              @click="goToPage(currentPage - 1)">
              <ChevronLeft :size="16" />
              Prev
            </button>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <button
              class="btn btn-sm btn-ghost"
              :disabled="currentPage >= totalPages"
              @click="goToPage(currentPage + 1)">
              Next
              <ChevronRight :size="16" />
            </button>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.layout {
  display: grid;
  grid-template-columns: 225px 1fr;
  grid-template-areas: 'sidebar main';
}

.sidebar {
  grid-area: sidebar;
}

.main {
  grid-area: main;
}

.aside {
  grid-area: aside;
}
</style>
